import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Minus,
  Plus,
  Trash2,
  ArrowRight,
  ArrowLeft,
  ShoppingBag,
  ShieldCheck,
  Truck,
  RotateCcw,
  Sparkles,
  AlertTriangle,
  AlertCircle,
} from "lucide-react";
import { useCart } from "../context/CartContext";
import { formatMoney, formatProductSize } from "../utils/format";
import { resolveImageUrl } from "../utils/images";
import { useLanguage } from "../context/LanguageContext";
import * as productsApi from "../api/productsApi";
import CouponBox from "../components/CouponBox";
import {
  FREE_SHIPPING_THRESHOLD,
  getFreeShippingRemaining,
  getFreeShippingProgress,
  isFreeShippingEligible,
} from "../constants/shipping";
import "./Cart.css";

/**
 * Extracts normalized product ID from either guest cart item or server cart item.
 */
function getCartItemProductId(item) {
  if (!item) return null;
  if (item.productId) return String(item.productId).trim().toLowerCase();
  if (item.product?.id) return String(item.product.id).trim().toLowerCase();
  if (typeof item.id === "string" && item.id.includes("_")) {
    const part = item.id.split("_")[0];
    if (part) return part.trim().toLowerCase();
  }
  return item.id ? String(item.id).trim().toLowerCase() : null;
}

/**
 * Normalizes differences between server cart item DTOs and guest cart items
 * into a consistent presentation view model.
 */
function getCartItemViewModel(item, isGuest, isRTL, language) {
  if (isGuest) {
    const unitPrice = Number(item.price) || 0;
    const quantity = Math.max(1, Number(item.quantity ?? item.qty ?? 1));
    const lineTotal = unitPrice * quantity;
    const formattedSize = item.sizeName ? formatProductSize(item.sizeName, language) : null;
    const variantText = [formattedSize, item.colorName].filter(Boolean).join(" · ");
    const imgUrl = item.imageUrl ? resolveImageUrl(item.imageUrl) : null;

    return {
      id: item.id,
      name: (isRTL && item.arabicName) ? item.arabicName : (item.name || (isRTL ? "منتج" : "Product")),
      unitPrice,
      quantity,
      lineTotal,
      variantText: variantText || (isRTL ? "منسوجات فاخرة" : "Luxury Terry"),
      imgUrl,
      colorHex: item.colorHex || "var(--cream)",
    };
  }

  // Server cart item
  const unitPrice = Number(item.product?.basePrice) || 0;
  const quantity = Math.max(1, Number(item.quantity) || 1);
  const lineTotal = unitPrice * quantity;
  const rawSize = item.sizeVariant?.size;
  const formattedSize = rawSize ? formatProductSize(rawSize, language) : null;
  const variantText = [formattedSize, item.colorVariant?.color]
    .filter(Boolean)
    .join(" · ");
  const imgUrl = item.colorVariant?.url ? resolveImageUrl(item.colorVariant.url) : null;

  return {
    id: item.id,
    name: (isRTL && item.product?.arabicName) ? item.product.arabicName : (item.product?.name || (isRTL ? "منتج" : "Product")),
    unitPrice,
    quantity,
    lineTotal,
    variantText: variantText || (isRTL ? "منسوجات فاخرة" : "Luxury Terry"),
    imgUrl,
    colorHex: item.colorVariant?.colorHex || "var(--cream)",
  };
}

export default function Cart() {
  const { t, isRTL, language } = useLanguage();
  const {
    isGuest,
    cartItems,
    subtotal,
    discountAmount,
    total,
    appliedCoupon,
    updateQuantity,
    removeFromCart,
    loadingServerCart,
    cartVolume,
    maxPackageVolume,
    isOverPackageLimit,
    canIncreaseItem,
  } = useCart();

  // Load product-to-category mapping for sorting
  const [categoryMap, setCategoryMap] = useState(() => new Map());

  useEffect(() => {
    let cancelled = false;
    productsApi
      .fetchProductCategoriesMap()
      .then((map) => {
        if (!cancelled && map) {
          setCategoryMap(map);
        }
      })
      .catch((err) => {
        console.warn("Could not load product categories for sorting:", err);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Show loading spinner only for authenticated users waiting on server cart
  if (!isGuest && loadingServerCart && (!cartItems || cartItems.length === 0)) {
    return (
      <div className="cart-page">
        <div className="page-loading">
          <span className="page-loading__dot" />
        </div>
      </div>
    );
  }

  const rawItems = cartItems || [];

  // Sort products by category displayOrder (from categories metadata)
  const items = useMemo(() => {
    if (!rawItems || rawItems.length <= 1) return rawItems;

    return [...rawItems].sort((a, b) => {
      const idA = getCartItemProductId(a);
      const idB = getCartItemProductId(b);

      const infoA = idA ? categoryMap.get(idA) : null;
      const infoB = idB ? categoryMap.get(idB) : null;

      const orderA =
        typeof a.displayOrder === "number"
          ? a.displayOrder
          : typeof a.product?.displayOrder === "number"
          ? a.product.displayOrder
          : typeof infoA?.displayOrder === "number"
          ? infoA.displayOrder
          : 9999;

      const orderB =
        typeof b.displayOrder === "number"
          ? b.displayOrder
          : typeof b.product?.displayOrder === "number"
          ? b.product.displayOrder
          : typeof infoB?.displayOrder === "number"
          ? infoB.displayOrder
          : 9999;

      if (orderA !== orderB) {
        return orderA - orderB;
      }

      // Secondary tie-breaker: group by category name
      const catA = a.categoryName || infoA?.categoryName || "";
      const catB = b.categoryName || infoB?.categoryName || "";
      if (catA !== catB) {
        return catA.localeCompare(catB);
      }

      // Tertiary tie-breaker: sort by product name
      const nameA = a.name || a.product?.name || "";
      const nameB = b.name || b.product?.name || "";
      return nameA.localeCompare(nameB);
    });
  }, [rawItems, categoryMap]);

  const freeShippingRemaining = getFreeShippingRemaining(subtotal, FREE_SHIPPING_THRESHOLD);
  const freeShippingProgress = getFreeShippingProgress(subtotal, FREE_SHIPPING_THRESHOLD);
  const hasFreeShipping = isFreeShippingEligible(subtotal, FREE_SHIPPING_THRESHOLD);

  return (
    <div className="cart-page">
      <div className="container cart-page__container">
        {/* Checkout Step Trail */}
        <nav className="cart-steps" aria-label="Checkout flow">
          <div className="cart-step cart-step--active">
            <span className="cart-step__num">1</span>
            <span className="cart-step__label">{t("cart.stepBag")}</span>
          </div>
          <span className="cart-steps__divider" />
          <div className="cart-step cart-step--inactive">
            <span className="cart-step__num">2</span>
            <span className="cart-step__label">{t("cart.stepCheckout")}</span>
          </div>
          <span className="cart-steps__divider" />
          <div className="cart-step cart-step--inactive">
            <span className="cart-step__num">3</span>
            <span className="cart-step__label">{t("cart.stepDone")}</span>
          </div>
        </nav>

        {/* Compact Integrated Page Header */}
        <div className="cart-header">
          <div className="cart-header__title-group">
            <h1 className="cart-header__title">{t("cart.title")}</h1>
            <span className="cart-header__count-badge">
              {items.length} {isRTL ? (items.length === 1 ? "قطعة" : "قطع") : (items.length === 1 ? "item" : "items")}
            </span>
            {isGuest && items.length > 0 && (
              <span className="cart-header__guest-badge">
                {isRTL ? "سلة زائر" : "Guest"}
              </span>
            )}
          </div>
          <Link to="/shop" className="cart-header__continue-link">
            {isRTL ? <ArrowRight size={15} /> : <ArrowLeft size={15} />}
            <span>{t("cart.continueShopping")}</span>
          </Link>
        </div>

        {items.length === 0 ? (
          /* Modern Empty Bag State */
          <div className="cart-empty-wrapper">
            <div className="cart-empty">
              <div className="cart-empty__icon-ring">
                <ShoppingBag size={44} strokeWidth={1.25} />
              </div>
              <h2 className="cart-empty__title">{t("cart.empty")}</h2>
              <p className="cart-empty__desc">{t("cart.emptyDesc")}</p>
              <Link to="/shop" className="btn-primary cart-empty__cta">
                <span>{t("action.shopNow")}</span>
                <ArrowRight size={15} className="rtl-flip" />
              </Link>

              <div className="cart-empty__suggestions">
                <span className="cart-empty__sugg-label">{t("cart.explorePrompt")}</span>
                <div className="cart-empty__pills">
                  <Link to="/shop" className="cart-empty__pill">
                    {isRTL ? "جميع التشكيلات" : "All Collections"}
                  </Link>
                  <Link to="/shop/bathrobes" className="cart-empty__pill">
                    {isRTL ? "الأرواب الفاخرة" : "Luxury Bathrobes"}
                  </Link>
                  <Link to="/shop/towels" className="cart-empty__pill">
                    {isRTL ? "المناشف والقطن" : "Towels & Linens"}
                  </Link>
                </div>
              </div>

              {isGuest && (
                <p className="cart-empty__guest-hint">
                  {isRTL ? "لديك حساب بالفعل؟ " : "Have an account? "}
                  <Link to="/login">{t("nav.signIn")}</Link>
                  {isRTL ? " لمتابعة سلتك وطلباتك المحفوظة." : " to access your saved bag and orders."}
                </p>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Free Delivery Milestone Meter */}
            <div className={`cart-free-shipping ${hasFreeShipping ? "cart-free-shipping--unlocked" : ""}`}>
              <div className="cart-free-shipping__header">
                <div className="cart-free-shipping__icon-box">
                  {hasFreeShipping ? <Sparkles size={16} /> : <Truck size={16} />}
                </div>
                <p className="cart-free-shipping__text">
                  {hasFreeShipping
                    ? t("cart.freeShippingUnlocked")
                    : t("cart.freeShippingRemaining").replace("{amount}", formatMoney(freeShippingRemaining, language))}
                </p>
              </div>
              <div
                className="cart-free-shipping__track"
                role="progressbar"
                aria-valuenow={freeShippingProgress}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className="cart-free-shipping__fill"
                  style={{ width: `${freeShippingProgress}%` }}
                />
              </div>
            </div>

            {/* Package Limit Exceeded Warning Banner */}
            {isOverPackageLimit && (
              <div className="cart-package-limit-alert" role="alert">
                <div className="cart-package-limit-alert__icon">
                  <AlertTriangle size={22} />
                </div>
                <div className="cart-package-limit-alert__content">
                  <h3 className="cart-package-limit-alert__title">
                    {t("cart.packageLimitExceededBannerTitle")}
                  </h3>
                  <p className="cart-package-limit-alert__desc">
                    {t("cart.packageLimitExceededBannerDesc").replace(
                      "{maxVolume}",
                      maxPackageVolume.toLocaleString()
                    )}
                  </p>
                </div>
              </div>
            )}

            {/* Cart Layout: Items + Order Summary */}
            <div className="cart-layout">
              <div className="cart-items-card">
                <div className="cart-items__head">
                  <span className="cart-items__col-product">{t("cart.product")}</span>
                  <span className="cart-items__col-price">{t("cart.unitPrice")}</span>
                  <span className="cart-items__col-qty">{t("product.quantity")}</span>
                  <span className="cart-items__col-total">{t("cart.total")}</span>
                  <span className="cart-items__col-action" />
                </div>

                <div className="cart-items__list">
                  {items.map((item) => {
                    const vm = getCartItemViewModel(item, isGuest, isRTL, language);
                    return (
                      <div key={vm.id} className="cart-item">
                        <div className="cart-item__product">
                          <div
                            className="cart-item__img"
                            style={
                              vm.imgUrl
                                ? { backgroundImage: `url(${vm.imgUrl})` }
                                : { backgroundColor: vm.colorHex }
                            }
                          />
                          <div className="cart-item__info">
                            <h3 className="cart-item__name">{vm.name}</h3>
                            <div className="cart-item__variant-badge">{vm.variantText}</div>
                            <span className="cart-item__mobile-price">
                              {formatMoney(vm.unitPrice, language)}
                            </span>
                          </div>
                        </div>

                        <div className="cart-item__price">
                          {formatMoney(vm.unitPrice, language)}
                        </div>

                        <div className="cart-item__qty">
                          <button
                            type="button"
                            className="cart-item__qty-btn"
                            onClick={() => updateQuantity(vm.id, vm.quantity - 1)}
                            aria-label="Decrease quantity"
                          >
                            <Minus size={13} />
                          </button>
                          <span className="cart-item__qty-value">{vm.quantity}</span>
                          <button
                            type="button"
                            className="cart-item__qty-btn"
                            onClick={() => updateQuantity(vm.id, vm.quantity + 1)}
                            disabled={!canIncreaseItem(vm.id)}
                            title={!canIncreaseItem(vm.id) ? t("cart.packageLimitReached") : undefined}
                            aria-label="Increase quantity"
                          >
                            <Plus size={13} />
                          </button>
                        </div>

                        <div className="cart-item__total">
                          {formatMoney(vm.lineTotal, language)}
                        </div>

                        <button
                          type="button"
                          className="cart-item__remove-btn"
                          onClick={() => removeFromCart(vm.id)}
                          title={t("cart.remove")}
                          aria-label={t("cart.remove")}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Order Summary Sticky Sidebar */}
              <aside className="cart-summary" aria-label={t("cart.orderSummary")}>
                <div className="cart-summary__header">
                  <h2 className="cart-summary__title">{t("cart.orderSummary")}</h2>
                  <span className="cart-summary__badge">
                    {items.length} {isRTL ? "عناصر" : "items"}
                  </span>
                </div>

                <div className="cart-summary__rows">
                  <div className="cart-summary__row">
                    <span>{t("cart.subtotal")}</span>
                    <span className="cart-summary__val">{formatMoney(subtotal, language)}</span>
                  </div>
                  {discountAmount > 0 && appliedCoupon && (
                    <div className="cart-summary__row cart-summary__row--discount">
                      <span className="cart-summary__discount-label">
                        <span>{t("coupon.discount")}</span>
                        <span className="cart-summary__coupon-code">({appliedCoupon.code})</span>
                      </span>
                      <span className="cart-summary__discount-val">
                        -{formatMoney(discountAmount, language)}
                      </span>
                    </div>
                  )}
                  <div className="cart-summary__divider" />
                  <div className="cart-summary__row cart-summary__total-row">
                    <span>{t("cart.total")}</span>
                    <span className="cart-summary__total-val">{formatMoney(total, language)}</span>
                  </div>
                </div>

                {/* Anti-Churn Collapsible Promo Code Accordion */}
                <CouponBox />

                {/* Checkout CTA */}
                <div className="cart-summary__actions">
                  {isOverPackageLimit && (
                    <div className="cart-summary__limit-warning" role="alert">
                      <AlertCircle size={15} />
                      <span>{t("cart.mustReduceItemsToCheckout")}</span>
                    </div>
                  )}

                  {isGuest ? (
                    <>
                      {isOverPackageLimit ? (
                        <button
                          type="button"
                          disabled
                          className="cart-summary__checkout-btn cart-summary__checkout-btn--disabled"
                          aria-disabled="true"
                          title={t("cart.mustReduceItemsToCheckout")}
                        >
                          <span>{t("cart.proceedCheckout")}</span>
                          <ArrowRight size={16} className="rtl-flip" />
                        </button>
                      ) : (
                        <Link
                          to="/guest-checkout"
                          className="cart-summary__checkout-btn"
                        >
                          <span>{t("cart.proceedCheckout")}</span>
                          <ArrowRight size={16} className="rtl-flip" />
                        </Link>
                      )}
                      <p className="cart-summary__auth-hint">
                        {isRTL ? "لديك حساب بالفعل؟ " : "Already have an account? "}
                        <Link
                          to="/login"
                          state={{ from: { pathname: "/checkout" } }}
                          className="cart-summary__signin-link"
                        >
                          {t("nav.signIn")}
                        </Link>
                      </p>
                    </>
                  ) : (
                    isOverPackageLimit ? (
                      <button
                        type="button"
                        disabled
                        className="cart-summary__checkout-btn cart-summary__checkout-btn--disabled"
                        aria-disabled="true"
                        title={t("cart.mustReduceItemsToCheckout")}
                      >
                        <span>{t("cart.proceedCheckout")}</span>
                        <ArrowRight size={16} className="rtl-flip" />
                      </button>
                    ) : (
                      <Link to="/checkout" className="cart-summary__checkout-btn">
                        <span>{t("cart.proceedCheckout")}</span>
                        <ArrowRight size={16} className="rtl-flip" />
                      </Link>
                    )
                  )}
                </div>

                {/* Trust Guarantees */}
                <div className="cart-summary__trust">
                  <div className="cart-summary__trust-item">
                    <ShieldCheck size={16} />
                    <span>{t("cart.secureCheckout")}</span>
                  </div>
                  <div className="cart-summary__trust-item">
                    <Truck size={16} />
                    <span>{t("cart.fastDelivery")}</span>
                  </div>
                  <div className="cart-summary__trust-item">
                    <RotateCcw size={16} />
                    <span>{t("cart.easyReturns")}</span>
                  </div>
                </div>
              </aside>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
